// @flow

import React, { useState, useEffect } from "react";
import { localize } from "redux-i18n";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  LinearWizard,
  Step,
} from "@components";
import Employees from "./employees";
import ConfigureContact from "./configure-contact";
import type { NotificationContact } from "../../reducer/hierarchy/types";

type OwnProps = $Exact<{
  toggleModal: (
    $Exact<{
      visible: boolean,
      notificationContact?: *,
    }>,
  ) => () => void,
  onSubmit: (data: Object) => *,
  clientId: number,
  selectedNotificationContact: ?NotificationContact,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  wizardData: Array<Object>,
  formData: {
    recipient: {
      id: number | null,
    },
    isCascading: string | null,
    notificationType: string | null,
  },
}>;

export function AddEditContactModal(props: Props) {
  const [state, setState] = useState({
    wizardData: [],
    formData: {
      recipient: {
        id: null,
      },
      isCascading: null,
      notificationType: null,
    },
  });
  const isValid = !(
    state.formData.recipient.id &&
    state.formData.isCascading &&
    state.formData.notificationType
  );

  useEffect(() => {
    if (props.selectedNotificationContact) {
      const { isCascading, notificationType } = props.selectedNotificationContact;
      setState({
        ...state,
        wizardData: [{ isValid: true }, { isValid: true }],
        formData: {
          recipient: {
            id: props.selectedNotificationContact.id,
          },
          isCascading: (isCascading && isCascading.toString()) || "",
          notificationType,
        },
      });
    }
  }, [props.selectedNotificationContact]);

  function onWizardChange(wizardData: Array<Object> = []) {
    setState({
      ...state,
      wizardData: wizardData,
      formData: {
        recipient: {
          id: wizardData[0].id || props.selectedNotificationContact?.recipient?.id,
        },
        isCascading: ((wizardData[1] || {}).isCascading || "").toString(),
        notificationType: (wizardData[1] || {}).notificationType,
      },
    });
  }

  function onSubmitForm() {
    props.onSubmit(state.formData);
  }

  return (
    <Modal>
      <ModalHeader tag="h3" toggle={props.toggleModal({ visible: false })}>
        {props.selectedNotificationContact ? props.t("EDIT_CONTACT") : props.t("ADD_CONTACT")}
      </ModalHeader>
      <ModalBody className="p-0">
        <LinearWizard
          data={state.wizardData}
          defaultStep={props.selectedNotificationContact ? 2 : 1}
          onChange={onWizardChange}
        >
          <Step
            isReadonly={true}
            component={Employees}
            editable={false}
            title={
              props.selectedNotificationContact ? props.t("CONTACT") : props.t("SELECT_CONTACT")
            }
            clientId={props.clientId}
            selectedNotificationContact={props.selectedNotificationContact}
          />
          <Step
            isReadonly={true}
            component={ConfigureContact}
            editable={false}
            title={props.t("CONFIGURE")}
            selectedNotificationContact={props.selectedNotificationContact}
          />
        </LinearWizard>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.toggleModal({ visible: false })}>
          {props.t("CANCEL")}
        </Button>
        <Button
          materialIcon={props.selectedNotificationContact ? "save" : "add_box"}
          disabled={isValid}
          onClick={onSubmitForm}
        >
          {props.selectedNotificationContact ? props.t("SAVE") : props.t("ADD")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default localize()(AddEditContactModal);
